import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Calendar from './components/Calendar';
import FeatureFlags from './components/FeatureFlags';
import QueryVault from './components/QueryVault/QueryVault';
import CORSGenerator from './components/CORSGenerator/CORSGenerator';
import TenantProvisioning from './components/TenantProvisioning/TenantProvisioning';
import TenantOnboarding from './components/TenantOnboarding/TenantOnboarding';
import EnvScanner from './components/EnvScanner/EnvScanner';
import EnvInspector from './components/EnvInspector/Inspector';
import EnvCompare from './components/EnvInspector/Compare';
import CorsManager from './components/EnvInspector/CorsManager';
import EnvInjector from './components/EnvInjector/EnvInjector';
import EnvManager from './components/EnvManager/EnvManager';
import Deployments from './components/Deployments/Deployments';
import DeploymentsBeta from './components/DeploymentsBeta/DeploymentsBeta';
import ExtraDeployments from './components/ExtraDeployments/ExtraDeployments';
import AccessControl from './components/AccessControl/AccessControl';
import HealthCheck from './components/HealthCheck/HealthCheck';
import InactiveUsers from './components/InactiveUsers/InactiveUsers';
import UrlRegistry from './components/UrlRegistry/UrlRegistry';
import AvalaraLicensing from './components/AvalaraLicensing/AvalaraLicensing';
import EventDashboard from './components/EventDashboard/EventDashboard';
import EventWatcher from './components/EventWatcher/EventWatcher';
import TrainingRevert from './components/TrainingRevert/TrainingRevert';
import DatabaseExport from './components/DatabaseExport/DatabaseExport';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Login from './components/Login';
import ProtectedRoute, { PermissionRoute, AdminOnlyRoute } from './components/ProtectedRoute';

const PAGE_TITLES = {
  '/': 'Multi-Tenant Calendar',
  '/feature-flags': 'Feature Flags',
  '/query-vault': 'QueryVault SQL Editor',
  '/azure-variables': 'Azure Environment Variables',
  '/tenant-provisioning': 'Tenant Provisioning',
  '/tenant-onboarding': 'Tenant Onboarding',
  '/env-scanner': 'Environment Scanner',
  '/env-inspector': 'Env Inspector',
  '/env-compare': 'Env Compare',
  '/cors-manager': 'CORS Manager',
  '/env-injector': 'Env Injector',
  '/env-manager': 'Env Manager',
  '/deployments': 'Deployments',
  '/deployments-beta': 'Deployments (Beta)',
  '/extra-deployments': 'Extra Deployment',
  '/access-control': 'Access Control',
  '/health-check': 'Health Check',
  '/inactive-users': 'Inactive Users',
  '/url-registry': 'URL Registry',
  '/avalara-licensing': 'Avalara Licensing',
  '/event-dashboard': 'Event Dashboard',
  '/event-watcher': 'Event Watcher',
  '/training-revert': 'Training Revert',
  '/database-export': 'Database Export',
};

function AppContent() {
  const location = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const isLoginPage = location.pathname === '/login';
  const pageTitle = PAGE_TITLES[location.pathname] || 'Dashboard';

  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-slate-950 font-sans text-gray-900 dark:text-gray-100 transition-colors duration-200">
      {!isLoginPage && (
        <Sidebar isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen} />
      )}
      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen transition-all duration-300 min-w-0 overflow-hidden">
        {!isLoginPage && (
          <Header
            title={pageTitle}
            onMenuClick={() => setIsMobileOpen(true)}
          />
        )}
        <main className="flex-1 overflow-hidden min-w-0">
          <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/"
          element={
            <PermissionRoute>
              <Calendar />
            </PermissionRoute>
          }
        />
        <Route
          path="/feature-flags"
          element={
            <PermissionRoute>
              <FeatureFlags />
            </PermissionRoute>
          }
        />
        <Route
          path="/query-vault"
          element={
            <PermissionRoute>
              <QueryVault />
            </PermissionRoute>
          }
        />
        <Route
          path="/azure-variables"
          element={
            <PermissionRoute>
              <CORSGenerator />
            </PermissionRoute>
          }
        />
        <Route
          path="/tenant-provisioning"
          element={
            <PermissionRoute>
              <TenantProvisioning />
            </PermissionRoute>
          }
        />
        <Route
          path="/tenant-onboarding"
          element={
            <PermissionRoute>
              <TenantOnboarding />
            </PermissionRoute>
          }
        />
        <Route
          path="/env-scanner"
          element={
            <PermissionRoute>
              <EnvScanner />
            </PermissionRoute>
          }
        />
        <Route
          path="/env-inspector"
          element={
            <PermissionRoute>
              <EnvInspector />
            </PermissionRoute>
          }
        />
        <Route
          path="/env-compare"
          element={
            <PermissionRoute>
              <EnvCompare />
            </PermissionRoute>
          }
        />
        <Route
          path="/cors-manager"
          element={
            <PermissionRoute>
              <CorsManager />
            </PermissionRoute>
          }
        />
        <Route
          path="/env-injector"
          element={
            <PermissionRoute>
              <EnvInjector />
            </PermissionRoute>
          }
        />
        <Route
          path="/env-manager"
          element={
            <PermissionRoute>
              <EnvManager />
            </PermissionRoute>
          }
        />
        <Route
          path="/deployments"
          element={
            <PermissionRoute>
              <Deployments />
            </PermissionRoute>
          }
        />
        <Route
          path="/deployments-beta"
          element={
            <PermissionRoute>
              <DeploymentsBeta />
            </PermissionRoute>
          }
        />
        <Route
          path="/extra-deployments"
          element={
            <AdminOnlyRoute>
              <ExtraDeployments />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="/access-control"
          element={
            <AdminOnlyRoute>
              <AccessControl />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="/health-check"
          element={
            <ProtectedRoute>
              <HealthCheck />
            </ProtectedRoute>
          }
        />
        <Route
          path="/inactive-users"
          element={
            <PermissionRoute>
              <InactiveUsers />
            </PermissionRoute>
          }
        />
        <Route
          path="/url-registry"
          element={
            <PermissionRoute>
              <UrlRegistry />
            </PermissionRoute>
          }
        />
        <Route
          path="/avalara-licensing"
          element={
            <PermissionRoute>
              <AvalaraLicensing />
            </PermissionRoute>
          }
        />
        <Route
          path="/event-dashboard"
          element={
            <PermissionRoute>
              <EventDashboard />
            </PermissionRoute>
          }
        />
        <Route
          path="/event-watcher"
          element={
            <PermissionRoute>
              <EventWatcher />
            </PermissionRoute>
          }
        />
        <Route
          path="/training-revert"
          element={
            <PermissionRoute>
              <TrainingRevert />
            </PermissionRoute>
          }
        />
        <Route
          path="/database-export"
          element={
            <PermissionRoute>
              <DatabaseExport />
            </PermissionRoute>
          }
        />
        <Route path="/cors-generator" element={<Navigate to="/azure-variables" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function App() {
  console.log('[App] Component rendering');
  console.log('[App] Environment:', {
    nodeEnv: process.env.NODE_ENV,
    apiUrl: process.env.REACT_APP_API_URL || 'Not set (using default)'
  });

  return (
    <AuthProvider>
      <ThemeProvider>
        <Router>
          <AppContent />
        </Router>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;

